import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Select,
  Grid,
  MenuItem,
  InputLabel,
  FormControl
} from "@mui/material";
import TF from "../api/requests/tf_backend";
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';

const Holdings = () => {
  const [holdings, setHoldings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('tradingsymbol');
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [totalCurrentValue, setTotalCurrentValue] = useState(0);
  const [totalPnL, setTotalPnL] = useState(0);
  const [tradeDialog, setTradeDialog] = useState({
    open: false,
    holding: null,
    action: 'BUY',
    quantity: 0
  });

  useEffect(() => {
    const fetchHoldingsData = async () => {
      try {
        const fetchedData = await TF.getHoldings();
        setHoldings(fetchedData);
        
        // Calculate portfolio metrics
        const investment = fetchedData.reduce((sum, holding) => sum + ((holding.quantity+holding.t1_quantity) * holding.average_price), 0);
        const currentValue = fetchedData.reduce((sum, holding) => sum + ((holding.quantity+holding.t1_quantity) * holding.last_price), 0);
        const pnl = currentValue - investment;
        
        setTotalInvestment(investment);
        setTotalCurrentValue(currentValue);
        setTotalPnL(pnl);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to fetch holdings data:", error);
        setIsLoading(false);
      }
    };

    fetchHoldingsData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const isAsc = order === 'asc';
      if (typeof a[orderBy] === 'string') {
        return isAsc 
          ? a[orderBy].localeCompare(b[orderBy]) 
          : b[orderBy].localeCompare(a[orderBy]);
      }
      return isAsc 
        ? a[orderBy] - b[orderBy] 
        : b[orderBy] - a[orderBy];
    });
  };

  const handleTradeClick = (holding) => {
    setTradeDialog({
      open: true,
      holding,
      action: 'BUY',
      quantity: holding.quantity
    });
  };

  const handleTradeClose = () => {
    setTradeDialog({
      open: false,
      holding: null,
      action: 'BUY',
      quantity: 0
    });
  };

  const handleTrade = async () => {
    try {
      const { holding, action, quantity } = tradeDialog;
      await TF.placeOrder({
        tradingsymbol: holding.tradingsymbol,
        exchange: "NSE",
        transaction_type: action,
        quantity: parseInt(quantity),
        order_type: "MARKET",
        product: "CNC",
        variety: "regular"
      });
      
      alert("Order placed successfully!");
      handleTradeClose();
    } catch (error) {
      alert(error?.response?.data || "Failed to place order");
    }
  };

  if (isLoading) {
    return <Typography>Loading holdings data...</Typography>;
  }

  return (
    <div>
      <Typography variant="h4" sx={{ 
        fontSize: '1.25rem',
        fontWeight: 500,
        mb: 3,
        color: '#333'
      }}>
        Holdings (21)
      </Typography>
      
      {/* Portfolio Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ 
            p: 2,
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>Total Investment</Typography>
            <Typography sx={{ 
              fontSize: '1.5rem',
              fontWeight: 500,
              color: '#2962ff'
            }}>
              ₹{totalInvestment.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ 
            p: 2,
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>Current Value</Typography>
            <Typography sx={{ 
              fontSize: '1.5rem',
              fontWeight: 500
            }}>
              ₹{totalCurrentValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ 
            p: 2,
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>Day's P&L</Typography>
            <Typography sx={{ 
              fontSize: '1.5rem',
              fontWeight: 500,
              color: totalPnL >= 0 ? '#2e7d32' : '#d32f2f',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              ₹{totalPnL.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              <Typography component="span" sx={{ 
                fontSize: '0.875rem',
                color: totalPnL >= 0 ? '#2e7d32' : '#d32f2f'
              }}>
                {((totalPnL/totalInvestment) * 100).toFixed(2)}%
              </Typography>
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: 1
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>
                <TableSortLabel
                  active={orderBy === 'tradingsymbol'}
                  direction={orderBy === 'tradingsymbol' ? order : 'asc'}
                  onClick={() => handleRequestSort('tradingsymbol')}
                >
                  Instrument
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>Quantity</TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>Avg. Cost</TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>LTP</TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>Invested</TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>Cur. Val</TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>Day ch</TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>P&L</TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666' }}>Net Ch.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {console.log(holdings)}
            {sortData(holdings).map((holding, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  bgcolor: holding.pnl >= 0 ? 'rgba(232, 245, 233, 0.2)' : 'rgba(255, 235, 238, 0.2)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <TableCell>{holding.tradingsymbol}</TableCell>
                <TableCell>{holding.quantity+holding.t1_quantity}</TableCell>
                <TableCell>₹{holding.average_price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                <TableCell>₹{holding.last_price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                <TableCell sx={{ color: holding.pnl >= 0 ? 'success.main' : 'error.main' }}>
                  ₹{(((holding.quantity+holding.t1_quantity)*holding.average_price)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell>₹{((holding.quantity+holding.t1_quantity) * holding.last_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                <TableCell sx={{ color: holding.day_change_percentage >= 0 ? 'success.main' : 'error.main' }}>
                  {holding.day_change_percentage.toFixed(2)}%
                </TableCell>
                <TableCell sx={{ color: holding.last_price-holding.average_price >= 0 ? 'success.main' : 'error.main' }}>
                  {((holding.last_price-holding.average_price)).toFixed(2)}
                </TableCell>
                <TableCell sx={{ color:  ((holding.last_price-holding.average_price)/holding.average_price*100)%((holding.quantity+holding.t1_quantity)*holding.average_price)>= 0 ? 'success.main' : 'error.main' }}>
                  {((((holding.last_price-holding.average_price)/holding.average_price*100)%((holding.quantity+holding.t1_quantity)*holding.average_price) ).toFixed(2))}
                </TableCell>
                <TableCell>
                  <Button 
                    variant="contained"
                    onClick={() => handleTradeClick(holding)}
                    size="small"
                  >
                    Trade
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Trade Dialog */}
      <Dialog open={tradeDialog.open} onClose={handleTradeClose}>
        <DialogTitle>
          Trade {tradeDialog.holding?.tradingsymbol}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Action</InputLabel>
            <Select
              value={tradeDialog.action}
              onChange={(e) => setTradeDialog(prev => ({ ...prev, action: e.target.value }))}
            >
              <MenuItem value="BUY">Buy</MenuItem>
              <MenuItem value="SELL">Sell</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            margin="normal"
            label="Quantity"
            type="number"
            value={tradeDialog.quantity+tradeDialog.t1_quantity}
            onChange={(e) => setTradeDialog(prev => ({ ...prev, quantity: e.target.value }))}
            inputProps={{ min: 0 }}
          />
          
          {tradeDialog.holding && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Current Price: ₹{tradeDialog.holding.last_price.toFixed(2)}
              <br />
              Total Value: ₹{(tradeDialog.holding.last_price * tradeDialog.quantity).toFixed(2)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTradeClose}>Cancel</Button>
          <Button 
            onClick={handleTrade} 
            variant="contained" 
            color={tradeDialog.action === 'BUY' ? 'primary' : 'error'}
          >
            {tradeDialog.action}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Holdings;